import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { JSONSchema7 } from 'json-schema';
import { participantPages } from '../participants.routes';
import { ParticipantService } from '@app/services/participants/participant.service';
import { ProjectDto } from '@api/projects/interfaces';
import { ProjectService } from '@app/services/project/project.service';
import { Router } from '@angular/router';
import { SessionItemData } from '@workflow/services/session-item-data';
import { of, Subscription } from 'rxjs';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { IParticipant, IParticipantConfig } from '@interfaces/interfaces';
import { InterpolationService, TextInterpolation } from '@app/services/interpolation/interpolation.service';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { ProtectedPrivacyService } from '@app/services/ui/protected-privacy.service';
import { MenuService } from '@app/services/menu';
import { catchError, switchMap } from 'rxjs/operators';
import { ParticipantRestService } from '@app/services/rest/participant-rest.service';
import { ErrorNotificationService } from '@app/services/error-notification/error-notification.service';
import { FormSchemaRendererComponent } from '@app/components/renderer/form-schema-renderer.component';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
	selector: 'app-participant-create',
	templateUrl: './participant-create.component.html',
	styles: [`
:host{
	display: block;
	overflow-x: hidden;
	overflow-y: auto;
	flex: 1 1 auto;
}`],
})
export class ParticipantCreateComponent implements OnInit, OnDestroy {
	private subscription = new Subscription();
	private participantInfoTemplate: TextInterpolation;
	activeProject: ProjectDto = null;

	data: SessionItemData = null;
	participantFormID = 'form-create-participant';
	readonly = false;
	resolvedSchema: JSONSchema7 = null;

	@ViewChild(FormSchemaRendererComponent)
	private form: FormSchemaRendererComponent;

	public userAddSplitButton: SplitButtonElement = {
		title: 'participants.create',
		icon: 'save',
		type: 'primary',
		buttonType: 'submit',
		attrForm: this.participantFormID,
	};
	public userCancelSplitButton: SplitButtonElement = {
		title: 'participants.cancel',
		icon: 'x',
		type: 'default',
		callback: () => this.showParticipantList(),
	};

	constructor(
		private projectService: ProjectService,
		private participantService: ParticipantService,
		private participantRestService: ParticipantRestService,
		private activeParticipantService: ActiveParticipantService,
		private protectedPrivacyService: ProtectedPrivacyService,
		private router: Router,
		private notification: NzNotificationService,
		private interpolationService: InterpolationService,
		private menuService: MenuService,
		private errorService: ErrorNotificationService,
	) {
	}

	ngOnInit(): void {
		this.subscription.add(
			this.projectService.activeProject$.subscribe(activeProject => {
				this.activeProject = activeProject;
			})
		);
		this.subscription.add(
			this.projectService.activeProject$.pipe(
				switchMap(activeProject => this.participantRestService.prepareParticipant$(activeProject.project_id).pipe(
					catchError((error: unknown) => {
						this.errorService.showErrorNotification(error);
						return of(<IParticipant>{});
					}),
				)),
			).subscribe(participant => {
				this.data = new SessionItemData(participant?.data || {});
			})
		);
		this.subscription.add(
			this.participantService.resolvedParticipantsConfigSchema$.subscribe(resolvedSchema => {
				this.resolvedSchema = resolvedSchema;
			})
		);
		this.subscription.add(
			this.projectService.participantsConfig$.subscribe(config => {
				this.loadParticipantDetailInfos(config);
			})
		);
		this.menuService.title = 'actions.new.participant';
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	async createNewParticipant(isFormValid: boolean): Promise<void> {
		if (!isFormValid) {
			return;
		}

		await this.participantService.createNewParticipant(
			this.activeProject, {id: null, data: this.data.data}
		).then((participant) => {
			const participantInfo = this.participantInfoTemplate?.interpolate(participant.data) || participant.id;
			this.notification.success('', `Benutzer ${participantInfo} erfolgreich angelegt`);
			this.participantService.refreshParticipants();
			this.activeParticipantService.activeParticipant = participant;
			if (this.protectedPrivacyService.statusProtected) {
				this.showParticipantDetails();
			} else {
				this.showParticipantList();
			}
		}).catch(error => {
			this.errorService.showErrorNotification(error);
			const property = error?.error?.property;
			const prefix = 'data.';
			if (typeof property === 'string' && property.startsWith(prefix)) {
				this.form.setValidationError(property.substring(prefix.length), error.error.message);
			}
		});
	}

	public showParticipantList(): void {
		this.router
			.navigate([participantPages.dashboard.base()])
			.catch(console.error);
	}

	public showParticipantDetails(): void {
		this.router
			.navigate([participantPages.stammDaten.base()])
			.catch(console.error);
	}

	public setSubmitActive(active: boolean): void {
		this.userAddSplitButton.disabled = !active;
	}

	private loadParticipantDetailInfos(config: IParticipantConfig) {
		const template = config?.details?.info?.name;
		this.participantInfoTemplate = template && this.interpolationService.interpolation(template);
	}
}
